import React, { useState } from "react";
import { Code2 } from "lucide-react";
import { AppCard, Callout, SegmentedControl } from "../ui";
import apiClient from "../../utils/apiClient";

const OPTIONS = [
  { value: "inherit", label: "Institute setting" },
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
];
const toChoice = (v) => (v === true ? "on" : v === false ? "off" : "inherit");
const fromChoice = { inherit: null, on: true, off: false };

/**
 * CodingPracticeCard — this course's override of the institute's Coding
 * Practice setting. Applies at once. The plan and the institute still decide
 * whether coding exists at all: "On" cannot switch on what they switched off.
 *
 * Props: courseId, value (null | true | false), onChange(next)
 */
const CodingPracticeCard = ({ courseId, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pick = async (choice) => {
    const next = fromChoice[choice];
    if (busy || next === (value ?? null)) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await apiClient.patch(`/courses/${courseId}/coding-feature`, { codingPractice: next });
      const saved = data?.course?.features?.codingPractice;
      onChange(saved === true || saved === false ? saved : null);
    } catch (err) {
      setError(err?.response?.data?.message || "The setting was not saved.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppCard as="section" aria-labelledby="sb-coding-heading">
      <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <h2 id="sb-coding-heading" className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-[0.9375rem] tw-font-semibold tw-text-content">
            <Code2 size={16} aria-hidden="true" className="tw-text-primary" /> Coding practice
          </h2>
          <p className="tw-m-0 tw-text-xs tw-text-content-muted">Coding questions and challenges for this course’s students and trainers.</p>
        </div>
        <SegmentedControl label="Coding practice for this course" value={toChoice(value)} onChange={pick} options={OPTIONS} />
        {error && <Callout tone="danger" role="alert">{error}</Callout>}
      </div>
    </AppCard>
  );
};

export default CodingPracticeCard;
